import { Popconfirm, Spin, theme } from 'antd';
import { useState } from 'react';
import cn from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import CircleIcon from '@/comp/common/icons/CircleIcon';
import VerticalLineIcon from '@/comp/common/icons/VerticalLineIcon';
import { EPButton } from '@/components';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { OnOffEnum } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';

import { usePowerOnOff } from './StartStopButtons.utils';

const { useToken } = theme;

const StartStopButtons = observer(
  ({
    deviceId,
    deviceType,
    poweredOn,
    hideLabels,
  }: {
    deviceId: string;
    deviceType: DeviceType;
    poweredOn: OnOffEnum;
    hideLabels?: boolean;
  }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const { controlCenterStore } = useStores();
    const [isPowerLoading, setIsPowerLoading] = useState(
      controlCenterStore.isPendingPoweredOnStateChange.get(deviceId),
    );

    const { shouldDisplayPowerOnOff, isPoweredOnDisabled, isStopButtonDisabled, isStartButtonDisabled, disableReason } =
      usePowerOnOff(deviceId, deviceType, poweredOn);

    const isStopLoading = !isStopButtonDisabled && isPowerLoading;
    const isStartLoading = !isStartButtonDisabled && isPowerLoading;

    const onStartStopClick = async () => {
      try {
        setIsPowerLoading(true);
        await controlCenterStore.toggleOnOff(deviceId);
      } finally {
        setIsPowerLoading(false);
      }
    };

    const stopPropagation = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div className={cn('flex-row-container', !shouldDisplayPowerOnOff && 'hidden')} onClick={stopPropagation} onMouseDown={stopPropagation}>
        <ConditionalToolTip condition={isPoweredOnDisabled} content={t(disableReason)} placement="bottom">
          <div onClick={stopPropagation} onMouseDown={stopPropagation}>
            <Popconfirm
              title={t('confirmStop')}
              description={t('confirmStopDescription')}
              okText={t('confirmStop')}
              placement="leftTop"
              onConfirm={onStartStopClick}
              disabled={isStopButtonDisabled}
            >
              <EPButton
                type="primary"
                danger
                icon={
                  isStopLoading ? (
                    <Spin size="small" />
                  ) : (
                    <CircleIcon color={isStopButtonDisabled ? token.colorTextDisabled : token.colorText} />
                  )
                }
                disabled={isStopButtonDisabled}
                onClick={stopPropagation}
              >
                {!hideLabels && (isStopLoading ? t('Stopping...') : t('STOP'))}
              </EPButton>
            </Popconfirm>
          </div>
        </ConditionalToolTip>
        <ConditionalToolTip condition={isPoweredOnDisabled} content={t(disableReason)} placement="bottom">
          <EPButton
            danger
            type="primary"
            className={!isStartButtonDisabled && 'color-success'}
            icon={
              isStartLoading ? (
                <Spin size="small" />
              ) : (
                <VerticalLineIcon color={isStartButtonDisabled ? token.colorTextDisabled : token.colorText} />
              )
            }
            disabled={isStartButtonDisabled}
            onClick={(e) => {
              stopPropagation(e);
              onStartStopClick();
            }}
          >
            {!hideLabels && (isStartLoading ? t('Starting...') : t('START'))}
          </EPButton>
        </ConditionalToolTip>
      </div>
    );
  },
);

export default StartStopButtons;
